import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { RecaptchaModule, RECAPTCHA_SETTINGS, RecaptchaSettings, RecaptchaLoaderService } from 'ng-recaptcha';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ApplicationRoutingModule } from './application/application-routing.module';
import { ApplicationComponent } from './application/application.component';
import { LandingComponent } from './application/landing/landing.component';
import { BasicComponent } from './application/basic/basic.component';
import { PersonalComponent } from './application/personal/personal.component';
import { EmploymentComponent } from './application/employment/employment.component';
import { ResidencyComponent } from './application/residency/residency.component';
import { ApplicantComponent } from './application/applicant/applicant.component';
import { BackgroundCheckComponent } from './application/background-check/background-check.component';
import { PaymentComponent } from './application/payment/payment.component';
import { AdditionalPropertyComponent } from './application/additional-property-base/additional-property/additional-property.component';
import { LoginComponent } from './public/login/login.component';
import { RegisterComponent } from './public/register/register.component';
import { NewpasswordComponent } from './public/newpassword/newpassword.component';
import { ConfirmComponent } from './public/confirm/confirm.component';

import { EventEmitterService } from '../services/event-emitter.service'
import { ProspectService } from '../services/prospect.service'
import { RenewalCogService } from '../services/renewalcog.service';
import { AccountService } from '../services/account.service';
import { ApplicationDataService } from '../services/application.service';
import { ErrorService } from '../services/error.service';
import { DocumentSignDataService } from '../services/document-sign.service';
import { EmployerDataService } from '../services/employer.service';
import { AncillaryItemsDataService } from '../services/ancillary-items.service';
import { RentablesDataService } from '../services/rentables.service';
import { RentalHistoryDataService } from '../services/rentalhistory.service';
import { PaymentDataService } from '../services/payment.service';
import { UserLoginService } from '../services/user-login.service';
import { UserRegistrationService } from '../services/user-registration.service';
import { LeaseService } from '../services/lease.service';
import { CognitoUtil } from '../services/cognito.service';
import { GoogleAuthService } from '../services/google-auth.service';
import { DynamoDBService } from '../services/ddb.service';
import { RenewalPrimaryEmployerService } from '../services/renewal-primary-employercog.service';
import { WorkOrderService } from '../services/work-order.service';
import { AuthGuard } from './auth-guard.service';
import { ForgotComponent, Forgot2Component } from './public/forgot/forgot.component';
import { ResendComponent } from './public/resend/resend.component';
import { LogoutComponent } from './public/logout/logout.component';
import { DashboardComponent } from './application/dashboard/dashboard.component';
import { ApplicantsComponent } from './application/applicants/applicants.component';
import { InvitationComponent } from './application/invitation/invitation.component';
import { DocumentsComponent } from './application/documents/documents.component';
import { VerifastApplicationComponent } from './application/verifast/verifast.component';
import { PublicComponent } from './public/public.component';
import { SmartHomeComponent } from './smarthome/smarthome.component';
import { NtvComponent } from './ntv/ntv.component';
import { NtvService } from 'services/ntv.service';
import { NtvErrorComponent } from './ntv/ntverror/ntverror.component';
import { CancelComponent } from './ntv/cancel/cancel.component';
import { TourDashboardComponent } from './prospect/tour-dashboard/tour-dashboard.component';
import { DropdownSelectDirective } from '../directives/dropdown-select.directive';
import { AccountManagementComponent } from './account/account-management/account-management.component';

import { ContactInfoCogComponent } from './renewal-cog/contact-info-cog/contact-info-cog.component';
import { RenewalErrorCogComponent } from './renewal-cog/renewal-error-cog/renewal-error-cog.component';
import { OtherOccupantCogComponent } from './renewal-cog/other-occupant-cog/other-occupant-cog.component';
import { RoommateInfoCogComponent } from './renewal-cog/roommate-info-cog/roommate-info-cog.component';
import { ChoosePlanCogComponent } from './renewal-cog/choose-plan-cog/choose-plan-cog.component';
import { RenewalDashboardCogComponent } from './renewal-cog/renewal-dashboard-cog/renewal-dashboard-cog.component';
import { RenewalCogComponent } from './renewal-cog/renewal-cog.component';
import { NonLegacyLeasesDashboardComponent } from './leases/non-legacy-leases-dashboard/non-legacy-leases-dashboard.component';
import { NonLegacyLeasesErrorComponent } from './leases/non-legacy-leases-error/non-legacy-leases-error.component';
import { DocumentSignComponent } from './document-sign-base/document-sign/document-sign.component';
import { ProspectComponent } from './prospect/prospect.component';
import { TourAgreeComponent } from './prospect/tour-agree/tour-agree.component';
import { OpenhouseSelectComponent } from './prospect/openhouse-select/openhouse-select.component';
import { TourSummaryComponent } from './prospect/tour-summary/tour-summary.component';
import { ShowingDateselectComponent } from './prospect/showing-dateselect/showing-dateselect.component';
import { ShowingTimeselectComponent } from './prospect/showing-timeselect/showing-timeselect.component';
import { SelftourBaseComponent } from './prospect/selftour-base/selftour-base.component';
import { UploadDlComponent } from './prospect/selftour-base/upload-dl/upload-dl.component';
import { ConfirmInfoComponent } from './prospect/selftour-base/confirm-info/confirm-info.component';
import { SubmitPaymentComponent } from './prospect/selftour-base/submit-payment/submit-payment.component';
import { ScheduleSelftourComponent } from './prospect/selftour-base/schedule-selftour/schedule-selftour.component';
import { SummarySelftourComponent } from './prospect/selftour-base/summary-selftour/summary-selftour.component';
import { SelftourVerifyBaseComponent } from './prospect/selftour-verify-base/selftour-verify-base.component';
import { VerifyLocationComponent } from './prospect/selftour-verify-base/verify-location/verify-location.component';
import { VerifyIdentityComponent } from './prospect/selftour-verify-base/verify-identity/verify-identity.component';
import { LocateAgreeComponent } from './prospect/selftour-verify-base/locate-agree/locate-agree.component';
import { VerifyConfirmedComponent } from './prospect/selftour-verify-base/verify-confirmed/verify-confirmed.component';
import { TourProspectVerifyComponent } from './prospect/tour-prospect-verify/tour-prospect-verify.component';
import { CompressionService } from 'services/compression.service';
import { PrimaryRenewalEmploymentComponent } from './renewal-cog/primary-renewal-employment-cog/primary-renewal-employment-cog.component';
import { RoommateConfirmEmployerComponent } from './renewal-cog//roommate-confirm-employer-cog/roommate-confirm-employer-cog.component';
import { HeaderComponent } from './header/header.component';
import { MatButtonModule } from '@angular/material/button';
import { MenuComponent } from './menu/menu.component';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { SplashComponent } from './public/splash/splash.component';
import { FooterComponent } from './footer/footer.component';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatSelectModule } from '@angular/material/select';
import { FavoritesComponent } from './favorites/favorites.component';
import { FavDashboardComponent } from './favorites/fav-dashboard/fav-dashboard.component';
import { TelephoneNumberInputMaskComponent } from './materials/telephoneNumberInputMask/telephone-number-input-mask.component';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TourSplashComponent } from './prospect/tour-splash/tour-splash.component';
import { AddApplicantDialogComponent } from './application/applicants/add-applicant-dialog/add-applicant-dialog.component';
import { AddApplicantInfoDialogComponent } from './application/applicants/add-applicant-info-dialog/add-applicant-info-dialog.component';
import { RemoveApplicantDialogComponent } from './application/applicants/remove-applicant-dialog/remove-applicant-dialog.component';
import { AddSelfTourToCalendarComponent } from './prospect/add-self-tour-to-calendar/add-self-tour-to-calendar.component';
import { AddTourToCalendarComponent } from './prospect/add-tour-to-calendar/add-tour-to-calendar.component';
import { MatRadioModule, MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { PlanSelectedDialogComponent } from './renewal-cog/plan-selected-dialog/plan-selected-dialog.component';
import { MapComponent } from './map/map.component';
import { TestimonialComponent } from './testimonial/testimonial.component';
import { MatListModule } from '@angular/material/list';
import { TwoYearsDialogComponent } from './application/residency/two-years-dialog/two-years-dialog.component';
import { DocumentSignBaseComponent } from './document-sign-base/document-sign-base.component';
import { DocumentFooterComponent } from './document-sign-base/document-footer/document-footer.component';
import { TwoYearsEmpDialogComponent } from './application/employment/two-years-emp-dialog/two-years-emp-dialog.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { AdditionalPropertyBaseComponent } from './application/additional-property-base/additional-property-base.component';
import { DuplicateAppDialogComponent } from './application/additional-property-base/additional-property/duplicate-app-dialog/duplicate-app-dialog.component';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { SmartHomeDashboardComponent } from './smarthome/smart-home-dashboard/smart-home-dashboard.component';
import { NumberCeilingRounderDirective } from '../directives/number-ceiling-rounder.directive';
import { MatTabsModule } from '@angular/material/tabs';
import { WorkOrderDashboardComponent } from './work-order/work-order-dashboard/work-order-dashboard.component';
import { WorkOrderComponent } from './work-order/work-order.component';
import { WorkOrderCreateComponent } from './work-order/work-order-create/work-order-create.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PricingCalendarComponent } from './application/basic/pricing-calendar/pricing-calendar.component';
import { NgxSimpleCalendarModule } from 'ngx-simple-calendar';
import { WorkOrderConfirmRequestDialogComponent } from './work-order/work-order-confirm-request-dialog/work-order-confirm-request-dialog.component';
import { WorkOrderGrantPteDialogComponent } from './work-order/work-order-grant-pte-dialog/work-order-grant-pte-dialog.component';
import { WorkOrderCreateGuardService } from 'services/work-order-create-guard.service';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { RentablesComponent } from './application/rentables/rentables.component';
import { RenewalReviewCogComponent } from './renewal-cog/renewal-review-cog/renewal-review-cog.component';
import { PrivateHeaderComponent } from './private-header/private-header.component';
import { PhoneDialerNotificationBarComponent } from './materials/phone-dialer-notification-bar/phone-dialer-notification-bar.component';
import { PipesModule } from 'pipes/pipes.module';
import { DocumentsUploadComponent } from './application/documents/documents-upload/documents-upload.component';
import { ImportFromBankDialogComponent } from './application/documents/import-from-bank-dialog/import-from-bank-dialog.component';
import { VerifastVerifyComponent } from './prospect/selftour-base/verifast-verify/verifast-verify.component';
import { ForgotEmailComponent } from './public/forgot-email/forgot-email.component';
import { VerifastIframeComponent } from './prospect/selftour-base/verifast-verify/verifast-iframe/verifast-iframe.component';
import { ANALYTICSSERVICE_TOKEN, AnalyticsService, MixpanelService } from '../services/analytics.service';
import { RentableItemTestDiaglogComponent } from './application/rentables/rentable-item-test-diaglog/rentable-item-test-diaglog.component';
import { BrokerHomeComponent } from './broker/broker-home/broker-home.component';
import { BrokerComponent } from './broker/broker.component';
import { ErrorComponent } from './prospect/selftour-verify-base/error/error.component';
import { NgxMaskModule, IConfig } from 'ngx-mask'
import { A11yModule } from '@angular/cdk/a11y';
import { ResidentMenuComponent } from './resident-menu/resident-menu.component';
import { IncomeRequirementsComponent } from './prospect/selftour-base/schedule-selftour/income-requirements/income-requirements.component'
import { InvestigativeConsumerReportComponent } from "./application/background-check/investigative-consumer-report/investigative-consumer-report.component";
import { MtxTooltipModule, TooltipPosition } from '@ng-matero/extensions/tooltip';


export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

@NgModule({
  declarations: [
    AppComponent,
    ApplicationComponent,
    LoginComponent,
    RegisterComponent,
    LandingComponent,
    BasicComponent,
    PersonalComponent,
    EmploymentComponent,
    ResidencyComponent,
    ApplicantComponent,
    BackgroundCheckComponent,
    PaymentComponent,
    AdditionalPropertyComponent,
    NewpasswordComponent,
    ConfirmComponent,
    ForgotComponent,
    Forgot2Component,
    ResendComponent,
    LogoutComponent,
    DashboardComponent,
    ApplicantsComponent,
    InvitationComponent,
    DocumentsComponent,
    VerifastApplicationComponent,
    PublicComponent,
    SmartHomeComponent,
    NtvComponent,
    NtvErrorComponent,
    CancelComponent,
    TourDashboardComponent,
    DropdownSelectDirective,
    ContactInfoCogComponent,
    RenewalErrorCogComponent,
    RenewalCogComponent,
    OtherOccupantCogComponent,
    RoommateInfoCogComponent,
    ChoosePlanCogComponent,
    RenewalDashboardCogComponent,
    AccountManagementComponent,
    RenewalDashboardCogComponent,
    NonLegacyLeasesDashboardComponent,
    NonLegacyLeasesErrorComponent,
    DocumentSignComponent,
    ProspectComponent,
    TourAgreeComponent,
    OpenhouseSelectComponent,
    TourSummaryComponent,
    ShowingDateselectComponent,
    ShowingTimeselectComponent,
    SelftourBaseComponent,
    UploadDlComponent,
    ConfirmInfoComponent,
    SubmitPaymentComponent,
    ScheduleSelftourComponent,
    SummarySelftourComponent,
    SelftourVerifyBaseComponent,
    VerifyLocationComponent,
    VerifyIdentityComponent,
    LocateAgreeComponent,
    VerifyConfirmedComponent,
    TourProspectVerifyComponent,
    PrimaryRenewalEmploymentComponent,
    RoommateConfirmEmployerComponent,
    HeaderComponent,
    MenuComponent,
    SplashComponent,
    FooterComponent,
    FavoritesComponent,
    FavDashboardComponent,
    TourSplashComponent,
    TelephoneNumberInputMaskComponent,
    AddApplicantDialogComponent,
    AddApplicantInfoDialogComponent,
    RemoveApplicantDialogComponent,
    TelephoneNumberInputMaskComponent,
    PlanSelectedDialogComponent,
    TelephoneNumberInputMaskComponent,
    AddSelfTourToCalendarComponent,
    AddTourToCalendarComponent,
    MapComponent,
    TestimonialComponent,
    TwoYearsDialogComponent,
    DocumentSignBaseComponent,
    DocumentFooterComponent,
    TwoYearsEmpDialogComponent,
    AdditionalPropertyBaseComponent,
    DuplicateAppDialogComponent,
    SmartHomeDashboardComponent,
    NumberCeilingRounderDirective,
    WorkOrderDashboardComponent,
    WorkOrderComponent,
    WorkOrderCreateComponent,
    PricingCalendarComponent,
    WorkOrderConfirmRequestDialogComponent,
    WorkOrderGrantPteDialogComponent,
    RentablesComponent,
    RenewalReviewCogComponent,
    PrivateHeaderComponent,
    PhoneDialerNotificationBarComponent,
    DocumentsUploadComponent,
    ImportFromBankDialogComponent,
    VerifastVerifyComponent,
    ForgotEmailComponent,
    VerifastIframeComponent,
    RentableItemTestDiaglogComponent,
    BrokerHomeComponent,
    BrokerComponent,
    ErrorComponent,
    ResidentMenuComponent,
    IncomeRequirementsComponent,
    InvestigativeConsumerReportComponent
  ],
  entryComponents: [
    AddApplicantDialogComponent,
    AddApplicantInfoDialogComponent
  ],
  imports: [
    A11yModule,
    NgxMaskModule.forRoot(),
    MatTabsModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    BrowserModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    CurrencyMaskModule,
    ApplicationRoutingModule,
    AppRoutingModule,
    RecaptchaModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatMomentDateModule,
    MatSelectModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatRadioModule,
    MatToolbarModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDialogModule,
    MatListModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatTooltipModule,
    MtxTooltipModule,
    GoogleTagManagerModule.forRoot({
      id: "GTM-5H7KD8K",
    }),
    NgxSimpleCalendarModule,
    PipesModule.forRoot()
  ],
  providers: [
    { provide: ANALYTICSSERVICE_TOKEN, useClass: MixpanelService, multi: true },
    AnalyticsService,
    AccountService,
    ProspectService,
    RenewalCogService,
    ApplicationDataService,
    ErrorService,
    DocumentSignDataService,
    EmployerDataService,
    AncillaryItemsDataService,
    RentablesDataService,
    RentalHistoryDataService,
    PaymentDataService,
    UserLoginService,
    UserRegistrationService,
    DynamoDBService,
    CognitoUtil,
    GoogleAuthService,
    LeaseService,
    NtvService,
    AuthGuard,
    EventEmitterService,
    CompressionService,
    RenewalPrimaryEmployerService,
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: '6LdAnuoZAAAAAKdaZ_tV8ixUcsEqAh35ygQ19OSW',
      } as RecaptchaSettings
    },
    RecaptchaLoaderService,
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'accent' }
    },
    WorkOrderService,
    {
      provide: 'WorkOrderCreateGuardService',
      useValue: (component: WorkOrderCreateComponent, currentRoute: ActivatedRouteSnapshot, currentState:
        RouterStateSnapshot, nextState: RouterStateSnapshot) => component.canDeactivate(nextState.url)
    }

  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
